import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { initNativeVoipPush, teardownNativeVoipPush } from "@/lib/native-voip-push";
import type { User, Session } from "@supabase/supabase-js";

const SESSION_RESTORE_TIMEOUT_MS = 5_000;

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthState>({
  user: null,
  session: null,
  isLoading: true,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
  });

  useEffect(() => {
    let isMounted = true;
    let initialSessionResolved = false;
    const resolveInitialSession = (session: Session | null) => {
      if (!isMounted || initialSessionResolved) return;
      initialSessionResolved = true;
      window.clearTimeout(sessionRestoreTimer);
      setState({
        user: session?.user ?? null,
        session,
        isLoading: false,
      });
      if (session?.user) window.setTimeout(() => void initNativeVoipPush(), 5_000);
    };

    const sessionRestoreTimer = window.setTimeout(() => {
      console.warn("[AuthContext] Session restore timed out — treating as signed out until auth recovers");
      resolveInitialSession(null);
    }, SESSION_RESTORE_TIMEOUT_MS);

    // Listen first so OTP SIGNED_IN events cannot be missed while the initial
    // cached session read is still resolving.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      initialSessionResolved = true;
      window.clearTimeout(sessionRestoreTimer);
      setState({
        user: session?.user ?? null,
        session,
        isLoading: false,
      });
      // P4: register APNs VoIP push token on auth (no-op on web / non-native).
      if (session?.user) {
        window.setTimeout(() => void initNativeVoipPush(), 5_000);
      } else {
        void teardownNativeVoipPush();
      }
      // On sign-out: reset the native onboarding flag so the intro carousel
      // re-appears for the next user on this device. Do NOT clear the OTP
      // progress key here - Supabase fires SIGNED_OUT on stale-token refresh
      // failures, which can happen mid-OTP-flow and would wipe the user's
      // in-progress verification, sending them back to phone entry and
      // burning a second OTP. OTP progress is owned solely by ConnectAuthPage.
      if (event === 'SIGNED_OUT') {
        try {
          localStorage.removeItem('elefa.native.onboarding.completed.v1');
        } catch { /* storage may be unavailable in private mode */ }
      }
    });

    const readInitialSession = () => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        resolveInitialSession(session);
      }).catch((error) => {
        console.warn("[AuthContext] Initial session read failed:", error);
        resolveInitialSession(null);
      });
    };
    readInitialSession();

    // Native resume relay: re-arm session restore on each foreground.
    // main.tsx dispatches 'elefa-auth-reset' on every native resume so a
    // long-backgrounded app that lost its in-memory session can recover
    // without waiting for the next user interaction.
    const onAuthReset = () => {
      initialSessionResolved = false;
      readInitialSession();
    };
    window.addEventListener("elefa-auth-reset", onAuthReset);

    return () => {
      isMounted = false;
      window.clearTimeout(sessionRestoreTimer);
      window.removeEventListener("elefa-auth-reset", onAuthReset);
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={state}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
